// Shared create/edit-form engine for the staff Zone + Exhibit orchestrators
// (review #8). Both forms drove identical, drift-prone stateful logic:
//   • a seed-once guard so F-F media mutations priming the React Query detail
//     cache don't re-run the seed effect and wipe unsaved title/description
//     typing;
//   • a `hasUnsavedChanges` diff that gates server-side narration generation
//     (narration is generated from the *saved* description);
//   • the per-language "description edited since load" nudge flags;
//   • archived / purged lock derivation + the code-locked reason string.
// Only the wire hooks, the extra fields, and the auxiliary state (image
// preview, QR url, code) differ per owner — those stay in the form via
// `seedForm` / `onSeed`. Snake_case wire fields per ADR-0020.
//
// ExhibitionForm deliberately does NOT use this hook: it is camelCase, has no
// narration / QR / seed-once flow, and carries a bespoke lifecycle surface, so
// it would need more opt-outs than it shares. It reuses <LockdownBanner> only.

import { useEffect, useRef, useState } from "react";

// The wire slice the engine reads directly. Zone and Exhibit both satisfy it
// (their `state` unions widen to string; the description fields are present).
export interface LockableEntity {
  id: string;
  state?: string | null;
  descriptionTc?: string | null;
  descriptionEn?: string | null;
  descriptionSc?: string | null;
}

// Every owner form's state carries the three description fields the narration
// nudge compares; the remaining fields are owner-specific and diffed by key.
export interface DescribedFormState {
  descriptionTc: string;
  descriptionEn: string;
  descriptionSc: string;
}

export interface EntityFormResult<TState> {
  formData: TState;
  patch: (slice: Partial<TState>) => void;
  isPurged: boolean;
  isLocked: boolean;
  codeLockedReason: string | null;
  hasUnsavedChanges: boolean;
  narrationDescriptionChanged: { tc: boolean; en: boolean; sc: boolean };
}

export function useEntityForm<
  TEntity extends LockableEntity,
  TState extends DescribedFormState,
>(opts: {
  entity: TEntity | undefined;
  isEdit: boolean;
  entityNoun: string;
  emptyForm: TState;
  seedForm: (entity: TEntity) => TState;
  onSeed?: (entity: TEntity) => void;
}): EntityFormResult<TState> {
  const { entity, isEdit, entityNoun, emptyForm, seedForm, onSeed } = opts;

  const [formData, setFormData] = useState<TState>(emptyForm);

  // Latch the latest seed callbacks in refs so the seed effect can depend only
  // on [entity, isEdit] — keeping the seed-once guard intact — without the
  // callbacks (fresh closures each render) forcing it to re-run every render.
  const seedFormRef = useRef(seedForm);
  seedFormRef.current = seedForm;
  const onSeedRef = useRef(onSeed);
  onSeedRef.current = onSeed;

  // Seed ONCE per entity id — not on every `entity` reference change. F-F media
  // mutations prime the React Query detail cache; without this guard the effect
  // would re-run and wipe unsaved typing (title/description plus the auxiliary
  // image / QR / code state seeded in `onSeed`).
  const seededIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEdit || !entity) return;
    if (seededIdRef.current === entity.id) return; // already seeded this entity
    seededIdRef.current = entity.id;
    setFormData(seedFormRef.current(entity));
    onSeedRef.current?.(entity);
  }, [entity, isEdit]);

  const patch = (slice: Partial<TState>) =>
    setFormData((prev) => ({ ...prev, ...slice }));

  const isArchived = entity?.state === "archived";
  const isPurged = entity?.state === "purged";
  const isLocked = isArchived || isPurged;

  // The 3-digit code locks once the entity leaves DRAFT — regenerate_code 409s
  // server-side. Surface it on the button instead of an error toast.
  const codeLockedReason =
    entity && entity.state && entity.state !== "draft"
      ? entity.state === "published"
        ? `Code is locked while the ${entityNoun} is published. Switch it back to Draft to rotate it.`
        : `Code is locked while the ${entityNoun} is ${entity.state}.`
      : null;

  // Unsaved edits vs the persisted entity. Narration generate reads the *saved*
  // description server-side, so it is blocked until the form is saved (otherwise
  // it would generate from stale text and the refetch would discard the typing).
  const savedRec = entity as unknown as
    | Record<string, string | null | undefined>
    | undefined;
  const formRec = formData as unknown as Record<
    string,
    string | null | undefined
  >;
  const hasUnsavedChanges =
    isEdit && savedRec
      ? Object.keys(formData).some(
          (k) => (formRec[k] ?? "") !== (savedRec[k] ?? ""),
        )
      : false;

  // #2: per-language "description edited since the saved version" — a live nudge
  // when narration audio already exists for that language.
  const narrationDescriptionChanged = {
    tc: formData.descriptionTc !== (entity?.descriptionTc ?? ""),
    en: formData.descriptionEn !== (entity?.descriptionEn ?? ""),
    sc: formData.descriptionSc !== (entity?.descriptionSc ?? ""),
  };

  return {
    formData,
    patch,
    isPurged,
    isLocked,
    codeLockedReason,
    hasUnsavedChanges,
    narrationDescriptionChanged,
  };
}
